import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaBookOpen, FaCheck, FaPen, FaPlus, FaSearch, FaTimes, FaTrash } from "react-icons/fa";
import ErrorPage from "../../../components/ErrorPage";
import Modal from "../../../components/Modal";
import Spinner from "../../../components/Spinner";
import { useCan } from "../../../authz/useCan";
import { archiveSubject, createSubject, listSubjects, updateSubject } from "../api";
import {
  ActionButton,
  AvatarBlock,
  EmptyState,
  Field,
  IconActionButton,
  LdsHeader,
  LdsPage,
  ListSection,
  SearchPanel,
  StatusBadge,
  cardEntryClass,
  inputClass,
  listRowClass,
} from "../components/Lds";
import type { AppError, EntityId, Subject } from "../types";
import { entityKey, httpStatus } from "../utils";

type FormState = {
  id: string | null;
  code: string;
  name: string;
  description: string;
  grades: number[];
  active: boolean;
};

const emptyForm: FormState = { id: null, code: "", name: "", description: "", grades: [], active: true };

const Subjects = () => {
  const { t } = useTranslation();
  const canCreate = useCan("school.subjects", "create");
  const canUpdate = useCan("school.subjects", "update");
  const canDelete = useCan("school.subjects", "delete");

  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [formOpen, setFormOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "active" | "archived">("all");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [subjectToArchive, setSubjectToArchive] = useState<EntityId | null>(null);
  const [error, setError] = useState<AppError | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setSubjects(await listSubjects());
    } catch (err) {
      setError({
        message: httpStatus(err) === 403
          ? t("error.403.message", { defaultValue: "You do not have permission." })
          : t("subjects.load_failed", { defaultValue: "Failed to load subjects." }),
        status: httpStatus(err),
      });
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { void load(); }, [load]);

  const filteredSubjects = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return [...subjects]
      .filter((subject) => statusFilter === "all" || (statusFilter === "active" ? subject.active : !subject.active))
      .filter((subject) => !query || `${subject.name} ${subject.code} ${subject.description ?? ""}`.toLowerCase().includes(query))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [searchQuery, statusFilter, subjects]);

  const openNew = () => { setForm(emptyForm); setFormOpen(true); };
  const openEdit = (subject: Subject) => {
    setForm({ id: String(subject.id), code: subject.code, name: subject.name, description: subject.description ?? "", grades: subject.grades ?? [], active: subject.active });
    setFormOpen(true);
  };
  const closeForm = () => { setForm(emptyForm); setFormOpen(false); };

  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    setSaving(true);
    try {
      const payload = { code: form.code, name: form.name, description: form.description, grades: form.grades, active: form.active };
      const saved = form.id ? await updateSubject(form.id, payload) : await createSubject(payload);
      setSubjects((current) => [...current.filter((item) => String(item.id) !== String(saved.id)), saved]);
      closeForm();
    } catch (err) {
      setError({ message: t("subjects.save_failed", { defaultValue: "Failed to save subject. Check whether the code and name are unique." }), status: httpStatus(err) });
    } finally {
      setSaving(false);
    }
  };

  const confirmArchive = async () => {
    if (subjectToArchive == null) return;
    try {
      const archived = await archiveSubject(subjectToArchive);
      setSubjects((items) => items.map((item) => String(item.id) === String(archived.id) ? archived : item));
      setSubjectToArchive(null);
    } catch (err) {
      setError({ message: t("subjects.archive_failed", { defaultValue: "Failed to archive subject." }), status: httpStatus(err) });
    }
  };

  if (loading && subjects.length === 0) return <Spinner />;
  if (error) return <ErrorPage message={error.message} status={error.status} onRetry={() => { setError(null); void load(); }} />;

  return (
    <LdsPage>
      <div className="space-y-5">
        <LdsHeader
          eyebrow={t("subjects.section", { defaultValue: "School structure" })}
          title={t("subjects.title", { defaultValue: "Subjects" })}
          description={t("subjects.subtitle", { defaultValue: "Manage subjects and the grades in which they are taught." })}
          meta={<><StatusBadge tone="success">{subjects.filter((subject) => subject.active).length} {t("common.active", { defaultValue: "Active" })}</StatusBadge><StatusBadge tone="neutral">{subjects.length} {t("subjects.total", { defaultValue: "Total" })}</StatusBadge></>}
          action={canCreate ? <ActionButton onClick={openNew}><span className="inline-flex items-center gap-2"><FaPlus aria-hidden="true" />{t("subjects.add", { defaultValue: "Add subject" })}</span></ActionButton> : undefined}
        />

        {formOpen && (form.id ? canUpdate : canCreate) ? (
          <form onSubmit={save} className={`${cardEntryClass} rounded-[26px] border border-indigo-100 bg-gradient-to-br from-white to-indigo-50/40 p-5 shadow-[0_18px_50px_rgba(79,70,229,0.10)] dark:border-indigo-400/20 dark:from-slate-950 dark:to-indigo-950/20 sm:p-6`}>
            <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
              <div className="flex items-start gap-3"><AvatarBlock><FaBookOpen aria-hidden="true" /></AvatarBlock><div><h2 className="text-lg font-bold text-slate-950 dark:text-white">{form.id ? t("subjects.edit", { defaultValue: "Edit subject" }) : t("subjects.new", { defaultValue: "New subject" })}</h2><p className="mt-1 text-sm text-slate-500 dark:text-slate-400">{t("subjects.form_hint", { defaultValue: "Define the subject and select the grades where it is offered." })}</p></div></div>
              <IconActionButton label={t("common.close", { defaultValue: "Close" })} onClick={closeForm}><FaTimes aria-hidden="true" /></IconActionButton>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <Field label={t("subjects.name", { defaultValue: "Name" })}><input className={inputClass} required value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} /></Field>
              <Field label={t("subjects.code", { defaultValue: "Code" })}><input className={inputClass} required maxLength={32} value={form.code} onChange={(event) => setForm({ ...form, code: event.target.value.toUpperCase() })} /></Field>
              <div className="md:col-span-2"><Field label={t("subjects.description", { defaultValue: "Description" })}><textarea className={`${inputClass} min-h-24 resize-y`} value={form.description} onChange={(event) => setForm({ ...form, description: event.target.value })} /></Field></div>
            </div>
            <fieldset className="mt-5"><legend className="text-xs font-semibold uppercase text-slate-500 dark:text-slate-400">{t("subjects.grades", { defaultValue: "Grades" })}</legend><p className="mt-1 text-sm text-slate-500 dark:text-slate-400">{t("subjects.grades_hint", { defaultValue: "Leave all grades unselected when the subject is available school-wide." })}</p><div className="mt-3 grid grid-cols-4 gap-2 sm:grid-cols-6 lg:grid-cols-12">{Array.from({ length: 12 }, (_, index) => index + 1).map((grade) => { const selected = form.grades.includes(grade); return <label key={grade} className={`flex cursor-pointer items-center justify-center rounded-2xl border px-3 py-3 text-sm font-bold transition ${selected ? "border-indigo-300 bg-indigo-50 text-indigo-800 shadow-sm dark:border-indigo-400/40 dark:bg-indigo-400/10 dark:text-indigo-100" : "border-slate-200 bg-white text-slate-600 hover:border-indigo-200 dark:border-white/10 dark:bg-white/5 dark:text-slate-300"}`}><input className="sr-only" type="checkbox" checked={selected} onChange={() => setForm({ ...form, grades: selected ? form.grades.filter((item) => item !== grade) : [...form.grades, grade] })} />{selected ? <FaCheck className="mr-1.5 text-xs" aria-hidden="true" /> : null}{grade}</label>; })}</div></fieldset>
            <div className="mt-6 flex flex-wrap justify-end gap-3 border-t border-slate-200 pt-4 dark:border-white/10"><ActionButton tone="secondary" onClick={closeForm}>{t("common.cancel", { defaultValue: "Cancel" })}</ActionButton><ActionButton type="submit" disabled={saving}><span className="inline-flex items-center gap-2"><FaCheck aria-hidden="true" />{saving ? t("common.saving", { defaultValue: "Saving..." }) : t("common.save", { defaultValue: "Save" })}</span></ActionButton></div>
          </form>
        ) : null}

        <SearchPanel>
          <div className="grid gap-3 lg:grid-cols-[minmax(240px,1fr)_auto] lg:items-center">
            <label className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-2 dark:border-white/10 dark:bg-slate-950/50"><FaSearch className="text-slate-400" aria-hidden="true" /><input className="min-w-0 flex-1 bg-transparent text-sm text-slate-950 outline-none placeholder:text-slate-400 dark:text-white" placeholder={t("subjects.search", { defaultValue: "Search subjects..." })} value={searchQuery} onChange={(event) => setSearchQuery(event.target.value)} /></label>
            <div className="flex flex-wrap gap-2">{(["all", "active", "archived"] as const).map((status) => <button key={status} type="button" onClick={() => setStatusFilter(status)} className={`rounded-full border px-4 py-2 text-sm font-semibold transition ${statusFilter === status ? "border-indigo-300 bg-indigo-50 text-indigo-800 dark:border-indigo-400/40 dark:bg-indigo-400/10 dark:text-indigo-100" : "border-slate-200 bg-white text-slate-600 dark:border-white/10 dark:bg-white/5 dark:text-slate-300"}`}>{t(`subjects.filters.${status}`, { defaultValue: status === "all" ? "All" : status === "active" ? "Active" : "Archived" })}</button>)}</div>
          </div>
        </SearchPanel>

        <ListSection title={t("subjects.overview", { defaultValue: "Subject overview" })} count={t("common.result_count", { count: filteredSubjects.length, defaultValue: "{{count}} results" })}>
          {filteredSubjects.map((subject) => (
            <article key={entityKey(subject.id)} className={`${listRowClass} cursor-default lg:grid-cols-[minmax(240px,1fr)_minmax(260px,1fr)_160px_auto]`}>
              <div className="flex min-w-0 items-center gap-3"><AvatarBlock tone="sky">{subject.code.slice(0, 3)}</AvatarBlock><div className="min-w-0"><h2 className="truncate text-base font-semibold text-slate-950 dark:text-white">{subject.name}</h2><p className="truncate text-sm text-slate-500 dark:text-slate-400">{subject.description || t("subjects.no_description", { defaultValue: "No description" })}</p></div></div>
              <div className="flex flex-wrap gap-1.5">{subject.grades.length ? [...subject.grades].sort((a, b) => a - b).map((grade) => <StatusBadge key={grade} tone="info">{t("subjects.grade", { defaultValue: "Grade {{grade}}", grade })}</StatusBadge>) : <StatusBadge tone="neutral">{t("subjects.all_grades", { defaultValue: "All grades" })}</StatusBadge>}</div>
              <StatusBadge tone={subject.active ? "success" : "neutral"}>{subject.active ? t("common.active", { defaultValue: "Active" }) : t("common.archived", { defaultValue: "Archived" })}</StatusBadge>
              <div className="flex items-center justify-start gap-2 lg:justify-end">{canUpdate ? <IconActionButton label={t("common.actions.edit", { defaultValue: "Edit" })} onClick={() => openEdit(subject)}><FaPen aria-hidden="true" /></IconActionButton> : null}{canDelete && subject.active ? <IconActionButton label={t("common.archive", { defaultValue: "Archive" })} tone="danger" onClick={() => setSubjectToArchive(subject.id)}><FaTrash aria-hidden="true" /></IconActionButton> : null}</div>
            </article>
          ))}
          {filteredSubjects.length === 0 ? <div className="p-5"><EmptyState>{t("subjects.empty", { defaultValue: "No subjects match the selected filters." })}</EmptyState></div> : null}
        </ListSection>
      </div>

      <Modal show={subjectToArchive != null} title={t("subjects.archive_title", { defaultValue: "Archive subject?" })} message={t("subjects.archive_message", { defaultValue: "The subject remains available in historical assignments, but can no longer be selected for new assignments." })} type="confirm" isConfirm onConfirm={confirmArchive} onClose={() => setSubjectToArchive(null)} />
    </LdsPage>
  );
};

export default Subjects;
